import type { CurrentMembership } from '@/stores/church-store';

import {
  hasAnyPermission,
  hasPermission,
  isAdminOrOwner,
  PERMISSIONS,
  type PermissionCode,
} from './permissions';
import {
  canAccessReportsModule,
  FINANCIAL_REPORT_ACCESS_PERMISSIONS,
  REPORT_ROUTE_ACCESS_PERMISSIONS,
} from './report-permissions';

/** Default view permission for a module's console navigation. */
export const MODULE_VIEW_PERMISSION: Partial<Record<string, PermissionCode>> = {
  people: PERMISSIONS.PEOPLE_VIEW,
  groups: PERMISSIONS.GROUPS_VIEW,
  programs: PERMISSIONS.PROGRAMS_VIEW,
  attendance: PERMISSIONS.ATTENDANCE_VIEW,
  giving: PERMISSIONS.GIVING_VIEW,
  projects: PERMISSIONS.PROJECTS_VIEW,
  spending: PERMISSIONS.EXPENSES_VIEW,
  budgets: PERMISSIONS.BUDGETS_VIEW,
  payments: PERMISSIONS.PAYMENTS_VIEW,
  messaging: PERMISSIONS.MESSAGING_VIEW,
  content: PERMISSIONS.CONTENT_VIEW,
  prayer: PERMISSIONS.PRAYER_VIEW,
  pastoral: PERMISSIONS.PASTORAL_VIEW,
  evangelism: PERMISSIONS.EVANGELISM_VIEW,
  community: PERMISSIONS.COMMUNITY_VIEW,
  documents: PERMISSIONS.DOCUMENTS_VIEW,
  sacraments: PERMISSIONS.SACRAMENTS_VIEW,
  assets: PERMISSIONS.ASSETS_VIEW,
  branches: PERMISSIONS.BRANCHES_VIEW,
  audit: PERMISSIONS.AUDIT_VIEW,
  welfare: PERMISSIONS.WELFARE_VIEW,
};

/** Modules that accept any one of several view permissions. */
export const MODULE_ANY_PERMISSION: Partial<Record<string, readonly PermissionCode[]>> = {
  reporting: REPORT_ROUTE_ACCESS_PERMISSIONS,
};

export const REPORTS_ACCESS_PERMISSIONS = REPORT_ROUTE_ACCESS_PERMISSIONS;

type PathAccessRule = {
  match: (path: string) => boolean;
  module: string;
  permission?: PermissionCode;
  anyPermission?: readonly PermissionCode[];
  /** Skip church-module enablement (billing-like always-on surfaces). */
  alwaysAvailable?: boolean;
};

/** Routes whose access differs from the module default. */
const NAV_PATH_OVERRIDES: PathAccessRule[] = [
  {
    match: (path) => path === '/home' || path === '/',
    module: 'reporting',
    anyPermission: REPORT_ROUTE_ACCESS_PERMISSIONS,
  },
  {
    match: (path) => path.startsWith('/reports'),
    module: 'reporting',
    anyPermission: REPORT_ROUTE_ACCESS_PERMISSIONS,
  },
  {
    match: (path) => path.startsWith('/financial-position'),
    module: 'spending',
    anyPermission: FINANCIAL_REPORT_ACCESS_PERMISSIONS,
  },
  {
    match: (path) => path.startsWith('/projects'),
    module: 'projects',
    anyPermission: [PERMISSIONS.PROJECTS_VIEW, PERMISSIONS.GIVING_VIEW],
  },
  {
    match: (path) => path.startsWith('/welfare'),
    module: 'welfare',
    permission: PERMISSIONS.WELFARE_VIEW,
  },
  {
    match: (path) => path.startsWith('/support'),
    module: 'support',
    permission: PERMISSIONS.SUPPORT_TICKETS_VIEW,
    alwaysAvailable: true,
  },
];

const resolvePathRule = (path: string) => NAV_PATH_OVERRIDES.find((rule) => rule.match(path));

export const getModuleAccessPermissions = (
  moduleCode: string,
): readonly PermissionCode[] | undefined => {
  const anyOf = MODULE_ANY_PERMISSION[moduleCode];
  if (anyOf) return anyOf;
  const single = MODULE_VIEW_PERMISSION[moduleCode];
  return single ? [single] : undefined;
};

export const canAccessSettings = (membership: CurrentMembership | null): boolean =>
  isAdminOrOwner(membership);

export const canAccessReports = (membership: CurrentMembership | null): boolean =>
  canAccessReportsModule(membership);

export const canAccessModule = (
  membership: CurrentMembership | null,
  moduleCode: string,
  isModuleEnabled: (code: string) => boolean,
): boolean => {
  if (!isModuleEnabled(moduleCode)) return false;
  if (isAdminOrOwner(membership)) return true;

  const accessPermissions = getModuleAccessPermissions(moduleCode);
  if (!accessPermissions) return true;
  return hasAnyPermission(membership, accessPermissions);
};

export const canAccessNavPath = (
  membership: CurrentMembership | null,
  moduleCode: string,
  path: string,
  isModuleEnabled: (code: string) => boolean,
): boolean => {
  const override = resolvePathRule(path);
  if (override) {
    if (!override.alwaysAvailable && !isModuleEnabled(override.module)) return false;
    if (override.anyPermission) return hasAnyPermission(membership, override.anyPermission);
    if (override.permission) return hasPermission(membership, override.permission);
    return true;
  }

  return canAccessModule(membership, moduleCode, isModuleEnabled);
};

/** @deprecated Use getModuleAccessPermissions — single permission is insufficient for some modules. */
export const getModuleViewPermission = (moduleCode: string): PermissionCode | undefined =>
  MODULE_VIEW_PERMISSION[moduleCode];
